/**
 * Company Settings Page - manage company members and invitations
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as companiesApi from '../services/companiesApi';
import { Company, CompanyMember, CompanyInvitation, CompanyRole } from '../types/company';

export const CompanySettingsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  
  const [company, setCompany] = useState<Company | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<CompanyRole>('member');
  const [inviting, setInviting] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  useEffect(() => {
    if (companyId) {
      loadCompanySettings();
    }
  }, [companyId]);

  const loadCompanySettings = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);
      
      const [companyData, membersData, invitationsData] = await Promise.all([
        companiesApi.getCompany(parseInt(companyId)),
        companiesApi.getCompanyMembers(parseInt(companyId)),
        companiesApi.listInvitations(parseInt(companyId))
      ]);

      setCompany(companyData);
      setMembers(membersData);
      setInvitations(invitationsData);
    } catch (err) {
      console.error('Error loading company settings:', err);
      setError('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !inviteEmail.trim()) return;

    try {
      setInviting(true);
      const invitation = await companiesApi.createInvitation(
        parseInt(companyId),
        { email: inviteEmail.trim(), role: inviteRole }
      );
      
      setInvitations([...invitations, invitation]);
      setInviteEmail('');
      setShowInviteForm(false);
      
      // Auto-copy invitation link
      const inviteLink = `${window.location.origin}/invitations/${invitation.token}`;
      await navigator.clipboard.writeText(inviteLink);
      setCopiedToken(invitation.token);
      setTimeout(() => setCopiedToken(null), 3000);
    } catch (err) {
      console.error('Error creating invitation:', err);
      alert('Failed to create invitation');
    } finally {
      setInviting(false);
    }
  };

  const copyInvitationLink = async (token: string) => {
    const inviteLink = `${window.location.origin}/invitations/${token}`;
    await navigator.clipboard.writeText(inviteLink);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 3000);
  };

  const handleDeleteInvitation = async (invitationId: number) => {
    if (!companyId) return;
    if (!window.confirm('Are you sure you want to delete this invitation?')) return;

    try {
      await companiesApi.deleteInvitation(parseInt(companyId), invitationId);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (err) {
      console.error('Error deleting invitation:', err);
      alert('Failed to delete invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-xl">{error || 'Company not found'}</p>
          <button onClick={() => navigate('/')} className="mt-4 btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <button
          onClick={() => navigate(`/companies/${companyId}`)}
          className="text-primary-600 hover:text-primary-700 mb-4"
        >
          ← Back to Company
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{company.name} Settings</h1>
      </div>

      {/* Members Section */}
      <div className="bg-white shadow-md rounded-lg mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Members</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {members.map((member) => (
            <div key={member.id} className="p-6 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">User ID: {member.user_id}</p>
                <p className="text-sm text-gray-500">Role: {member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invitations Section */}
      <div className="bg-white shadow-md rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Invitations</h2>
            {!showInviteForm && (
              <button
                onClick={() => setShowInviteForm(true)}
                className="btn btn-primary"
              >
                + Invite User
              </button>
            )}
          </div>

          {showInviteForm && (
            <form onSubmit={handleInvite} className="mt-4">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as CompanyRole)}
                    className="form-input"
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={inviting} className="btn btn-primary">
                    {inviting ? 'Creating...' : 'Create Invitation'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteEmail('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
        
        <div className="divide-y divide-gray-200">
          {invitations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No pending invitations</p>
            </div>
          ) : (
            invitations.map((invitation) => (
              <div key={invitation.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{invitation.email}</p>
                    <p className="text-sm text-gray-500">Role: {invitation.role}</p>
                    <p className="text-sm text-gray-500">Status: {invitation.status}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Expires: {new Date(invitation.expires_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyInvitationLink(invitation.token)}
                      className="btn btn-secondary text-sm"
                    >
                      {copiedToken === invitation.token ? '✓ Copied!' : 'Copy Link'}
                    </button>
                    <button
                      onClick={() => handleDeleteInvitation(invitation.id)}
                      className="btn btn-secondary text-sm text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
